import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, cn, MenuButton } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Check, Maximize2, Monitor } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductionOrderOperationClient, type ProductionOrderOperationDTO } from "../../api/floor";
import { formatNumber } from "../../api/manufacturing";
import { AssemblyOrderClient, type AssemblyBoardRowDTO, type AssemblyBoardStepDTO } from "../../api/production";
import { fetchSingleRow, SETTINGS_PATHS, settingsKeys, type ShopFloorSettingsDTO } from "../../api/settings";
import { OpenRecord } from "../../components/OpenRecord";
import { NexpimView } from "../../views";
import { PutAwayDialog, stepLabel } from "./assembly";
import { ProductionStatusBadge, SigmBadge } from "./badges";
import { StepDialog } from "./lot/StepDialog";
import { ReceiveTransferDialog } from "./ReceiveTransferDialog";

/** "Kit 200/200 ✓": one step's progress, compact, in its colour. */
function StepChip({ step, onOpen }: { step: AssemblyBoardStepDTO; onOpen?: () => void }) {
  const { t } = useTranslation();
  const done = step.status === "DONE" || step.status === "SKIPPED";
  const started = Number(step.quantity_done) > 0;
  const body = (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[12px] tabular-nums",
        done ? "border-ok/40 bg-ok-soft/40 text-ok" : started ? "border-info/40 text-info" : "border-line text-ink-muted",
      )}
    >
      <span className="font-medium">{stepLabel(t, step.process_name, step.box_number)}</span>
      {done ? (
        <Check className="size-3.5" strokeWidth={3} />
      ) : (
        <span>
          {formatNumber(step.quantity_done)}/{formatNumber(step.quantity_required)}
        </span>
      )}
    </span>
  );
  return onOpen ? (
    <button type="button" onClick={onOpen} className="rounded-md focus-visible:outline-2 focus-visible:outline-accent">
      {body}
    </button>
  ) : (
    body
  );
}

/**
 * Terrebonne: the assembly orders whose sets are on their way or have arrived,
 * on the office screen and the shop monitor.
 *
 * <p>Per order: what arrived of its production order's trucks, each step
 * (Hardware kit, Foam, Cornières, then packing per box) as done over what it
 * has, how many sets are ready to assemble (packing never gets ahead of the
 * slowest of the three), and what is packed. The acts: receive a truck, report
 * a step (as at a kiosk), put away what was packed. No shelf section: where
 * the boxes went is on each order.</p>
 *
 * <p>The TV mode (?tv=1) is the monitor: large type, full screen, scrolling on
 * its own, nothing to click.</p>
 */
export function TerrebonnePage({ tv }: { tv: boolean }) {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.TERREBONNE_PACKING);
  const editable = allowed && canWrite;
  const assembly = useMemo(() => new AssemblyOrderClient(http), [http]);
  const [receiving, setReceiving] = useState<AssemblyBoardRowDTO | null>(null);
  const [puttingAway, setPuttingAway] = useState<AssemblyBoardRowDTO | null>(null);
  const [reporting, setReporting] = useState<ProductionOrderOperationDTO | null>(null);

  const settings = useQuery({
    queryKey: settingsKeys.shopFloor,
    queryFn: () => fetchSingleRow<ShopFloorSettingsDTO>(http, SETTINGS_PATHS.shopFloor),
    enabled: allowed,
    staleTime: 10 * 60_000,
    retry: false,
  });
  const refreshSeconds = settings.data?.board_refresh_seconds ?? 60;

  const board = useQuery({
    queryKey: ["production", "assembly", "board"],
    queryFn: () => assembly.board(),
    enabled: allowed,
    refetchInterval: refreshSeconds * 1000,
  });

  const rows = useMemo(() => board.data ?? [], [board.data]);
  const open = rows.filter((row) => row.order.status !== "COMPLETED");
  const totalMinutes = open.reduce((sum, row) => sum + Number(row.packing_minutes ?? 0), 0);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["production"] });
  };
  // A step opened from the board is reported in the same dialog the order page uses.
  const openStep = async (operationId: number) => {
    setReporting(await new ProductionOrderOperationClient(http).findById(operationId));
  };

  const cols = useMemo(() => {
    const c = defineColumns<AssemblyBoardRowDTO>();
    return [
      c.custom(
        "order",
        (row) => row.order.number,
        (row) => (
          <span className="flex flex-col">
            <Link
              to="/production/orders/$orderId"
              params={{ orderId: String(row.order.id) }}
              className="font-medium tabular-nums text-accent underline-offset-2 hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.order.number}
            </Link>
            <span className="text-[11px] tabular-nums text-ink-faint">
              {t("tb.from-order", "from {{n}}", { n: row.order.fabrication_order_number ?? "" })}
            </span>
            {row.order.is_on_hold && (
              <Badge variant="error" size="sm">
                {t("tb.on-hold", "On hold")}
              </Badge>
            )}
          </span>
        ),
        { headerKey: "column.mo.number", priority: 1 },
      ),
      c.custom(
        "product",
        (row) => row.order.product_sku ?? "",
        (row) => (
          <span className="flex flex-col">
            <span className="flex items-center gap-1.5 font-medium">
              {row.order.product_sku}
              <Badge variant="neutral" size="sm">
                {row.order.version_code}
              </Badge>
              {row.order.is_from_sigm && <SigmBadge />}
            </span>
            <span className="text-[11px] text-ink-faint">{row.order.part_name ?? row.order.name}</span>
          </span>
        ),
        { headerKey: "column.sku", priority: 1 },
      ),
      c.custom(
        "status",
        (row) => row.order.status,
        (row) => <ProductionStatusBadge status={row.order.status} />,
        { headerKey: "column.status", priority: 2 },
      ),
      c.custom(
        "arrived",
        (row) => Number(row.order.firmed_quantity ?? 0),
        (row) => (
          <span className="flex flex-col text-[12px] tabular-nums">
            <span>
              {t("tb.arrived.sets", "{{firmed}} of {{quantity}}", {
                firmed: formatNumber(row.order.firmed_quantity ?? 0),
                quantity: formatNumber(row.order.quantity),
              })}
            </span>
            {row.open_transfers > 0 && <span className="text-[11px] text-warn">{t("tb.arrived.trucks", "{{n}} on the way", { n: row.open_transfers })}</span>}
          </span>
        ),
        { header: t("tb.column.arrived-sets", "Sets arrived"), priority: 1 },
      ),
      c.custom(
        "steps",
        (row) => row.steps.filter((step) => step.status === "DONE").length,
        (row) =>
          row.steps.length === 0 ? (
            <span className="text-[12px] text-ink-faint">{t("tb.steps.none", "Planned when its sets arrive")}</span>
          ) : (
            <span className="flex flex-wrap gap-1">
              {row.steps.map((step) => (
                <StepChip key={step.operation_id} step={step} onOpen={editable ? () => void openStep(step.operation_id) : undefined} />
              ))}
            </span>
          ),
        { header: t("tb.column.steps", "Steps"), priority: 1, enableSorting: false },
      ),
      c.custom(
        "ready",
        (row) => Number(row.ready),
        (row) =>
          Number(row.ready) > Number(row.packed) ? (
            <Badge variant="accent" size="sm">
              {t("tb.ready-to-assemble", "{{n}} ready to assemble", { n: formatNumber(Number(row.ready) - Number(row.packed)) })}
            </Badge>
          ) : (
            <span />
          ),
        { header: t("tb.column.ready", "Ready"), priority: 1 },
      ),
      c.custom(
        "packed",
        (row) => Number(row.packed),
        (row) => (
          <span className={cn("tabular-nums text-[12px]", Number(row.packed) >= Number(row.order.firmed_quantity ?? 0) && Number(row.packed) > 0 && "text-ok")}>
            {formatNumber(row.packed)} / {formatNumber(row.order.firmed_quantity ?? 0)}
          </span>
        ),
        { headerKey: "tb.column.packed", priority: 2 },
      ),
      c.custom(
        "priority",
        (row) => row.order.priority ?? Number.MAX_SAFE_INTEGER,
        (row) => <span className="tabular-nums">{row.order.priority ?? ""}</span>,
        { headerKey: "tb.column.priority", priority: 3, align: "right" },
      ),
      c.text("due_date", (row) => row.order.due_date ?? "", { headerKey: "column.mo.due", priority: 3 }),
      c.text("location", (row) => row.order.storage_location_code ?? row.order.sigm_location ?? "", { headerKey: "tb.column.location", priority: 2 }),
      c.custom(
        "minutes",
        (row) => Number(row.packing_minutes ?? 0),
        (row) => <span className="tabular-nums">{row.packing_minutes == null ? "" : formatNumber(row.packing_minutes)}</span>,
        { headerKey: "tb.column.minutes", priority: 3, align: "right" },
      ),
      c.text("notes", (row) => [row.order.floor_note, row.order.floor_comment].filter(Boolean).join(" · "), {
        headerKey: "tb.column.notes",
        priority: 4,
      }),
    ];
  }, [t, editable]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.terrebonne", "Terrebonne")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
        </div>
      </>
    );
  }

  const title = t("navigation.item.terrebonne", "Terrebonne");
  if (tv) {
    return (
      <TerrebonneTv
        title={title}
        rows={rows}
        loading={board.isLoading}
        totalMinutes={totalMinutes}
        onExit={() => void navigate({ to: "/assembly/floor", search: {} })}
      />
    );
  }

  const actions = (row: AssemblyBoardRowDTO) => {
    const working = row.order.status === "FIRMED" || row.order.status === "IN_PROGRESS";
    const toPlace = Number(row.packed) - Number(row.order.quantity_put_away ?? 0);
    const next =
      working && toPlace > 0
        ? { label: t("assembly.put-away.confirm", "Put away"), run: () => setPuttingAway(row) }
        : row.open_transfers > 0
          ? { label: t("tb.action.receive", "Receive a truck"), run: () => setReceiving(row) }
          : null;
    const items = [
      { label: t("tb.action.receive", "Receive a truck"), disabled: row.open_transfers === 0, onClick: () => setReceiving(row) },
      { label: t("assembly.put-away.confirm", "Put away"), disabled: !working || toPlace <= 0, onClick: () => setPuttingAway(row) },
    ];
    return (
      <span className="flex items-center gap-1">
        {editable && next && (
          <Button size="sm" variant="secondary" onClick={next.run}>
            {next.label}
          </Button>
        )}
        {editable && <MenuButton variant="ghost" label={t("tb.actions", "Actions")} items={items} />}
        <OpenRecord target={{ kind: "production-order", id: row.order.id }} label={String(row.order.number)} />
      </span>
    );
  };

  return (
    <>
      <PageHeader
        title={title}
        subtitle={t(
          "tb.subtitle.assembly",
          "Assembly orders whose sets are on their way or here: receive the trucks, report the kit, foam, cornières and packing, put away.",
        )}
        badges={
          <>
            <Badge variant="neutral" size="sm">
              {t("tb.badge.orders", "{{n}} orders", { n: open.length })}
            </Badge>
            <Badge variant="info" size="sm">
              {t("tb.badge.minutes", "{{n}} min of packing", { n: formatNumber(Math.round(totalMinutes)) })}
            </Badge>
          </>
        }
        actions={
          <Button variant="secondary" onClick={() => void navigate({ to: "/assembly/floor", search: { tv: 1 } })}>
            <Monitor className="size-4" />
            {t("tb.tv", "Monitor view")}
          </Button>
        }
      />
      <div className="flex min-h-0 flex-1 flex-col">
        <DataTable<AssemblyBoardRowDTO>
          data={rows}
          columns={cols}
          getRowId={(row) => String(row.order.id)}
          loading={board.isLoading}
          stateKey="/production/terrebonne/assembly"
          rowActions={actions}
          rowClassName={(row) => (row.order.status === "COMPLETED" ? "opacity-60" : undefined)}
          emptyMessage={t("tb.empty.assembly", "No assembly order has sets on their way or here.")}
        />
      </div>
      {receiving && (
        <ReceiveTransferDialog
          lotId={receiving.order.fabrication_order_id ?? 0}
          lotNumber={receiving.order.fabrication_order_number ?? 0}
          onClose={() => setReceiving(null)}
          onReceived={async () => {
            setReceiving(null);
            await refresh();
          }}
        />
      )}
      {puttingAway && <PutAwayDialog order={puttingAway.order} packed={Number(puttingAway.packed)} onClose={() => setPuttingAway(null)} />}
      {reporting && <StepDialog step={reporting} editable={editable} onClose={() => setReporting(null)} onChanged={refresh} />}
    </>
  );
}

/**
 * The shop monitor: the same orders in large type, scrolling down slowly and
 * starting over, the way the spreadsheet was shown on the TV. Full screen on
 * request (browsers only allow it from a click).
 */
function TerrebonneTv({
  title,
  rows,
  loading,
  totalMinutes,
  onExit,
}: {
  title: string;
  rows: AssemblyBoardRowDTO[];
  loading: boolean;
  totalMinutes: number;
  onExit: () => void;
}) {
  const { t } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    // Slow and steady: a line every few seconds, pausing at both ends.
    let pause = 0;
    const timer = window.setInterval(() => {
      if (pause > 0) {
        pause--;
        return;
      }
      if (element.scrollTop + element.clientHeight >= element.scrollHeight - 2) {
        pause = 6;
        element.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      element.scrollBy({ top: 2 });
    }, 60);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-surface text-ink">
      <header className="flex items-center gap-4 border-b border-line px-6 py-3">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <span className="text-lg text-ink-muted">
          {t("tb.badge.minutes", "{{n}} min of packing", { n: formatNumber(Math.round(totalMinutes)) })}
        </span>
        <span className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={() => void document.documentElement.requestFullscreen?.()}>
            <Maximize2 className="size-4" />
            {t("tb.tv.fullscreen", "Full screen")}
          </Button>
          <Button variant="ghost" onClick={onExit}>
            {t("tb.tv.exit", "Leave monitor view")}
          </Button>
        </span>
      </header>
      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <p className="p-6 text-xl text-ink-muted">{t("common.loading", "Loading…")}</p>
        ) : (
          <table className="w-full text-xl">
            <thead className="sticky top-0 z-10 bg-surface-sunk text-left text-base text-ink-muted">
              <tr>
                <th className="px-4 py-2 font-medium">{t("column.mo.number", "Lot")}</th>
                <th className="px-4 py-2 font-medium">{t("column.sku", "SKU")}</th>
                <th className="px-4 py-2 font-medium">{t("tb.column.arrived-sets", "Sets arrived")}</th>
                <th className="px-4 py-2 font-medium">{t("tb.column.steps", "Steps")}</th>
                <th className="px-4 py-2 font-medium">{t("tb.column.ready", "Ready")}</th>
                <th className="px-4 py-2 font-medium">{t("tb.column.packed", "Packed")}</th>
                <th className="px-4 py-2 font-medium">{t("tb.column.location", "Location")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.order.id} className={cn("border-t border-line align-top", row.order.status === "COMPLETED" && "opacity-50")}>
                  <td className="px-4 py-3 font-semibold tabular-nums">{row.order.number}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold">{row.order.product_sku}</span>
                    <span className="ml-2 text-base text-ink-muted">{row.order.version_code}</span>
                    <span className="block text-base text-ink-muted">{row.order.part_name ?? row.order.name}</span>
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {formatNumber(row.order.firmed_quantity ?? 0)} / {formatNumber(row.order.quantity)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex flex-wrap gap-1.5 text-base">
                      {row.steps.map((step) => (
                        <StepChip key={step.operation_id} step={step} />
                      ))}
                    </span>
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {Number(row.ready) > Number(row.packed) ? formatNumber(Number(row.ready) - Number(row.packed)) : ""}
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {formatNumber(row.packed)} / {formatNumber(row.order.firmed_quantity ?? 0)}
                  </td>
                  <td className="px-4 py-3 font-semibold">{row.order.storage_location_code ?? row.order.sigm_location ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
